import React, { useCallback, useEffect, useRef } from 'react'
import { Table } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { FeatherLoader } from '@abroad/components'
import { formatDateTimeWithTimezone } from '../../utils/date'
import SchedulingListTitle from './components/SchedulingListTitle'

const CompletedListView = ({
  schedulingList,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  timezone,
  clearFilter = null,
  isCalendarConnected = true,
}) => {
  const observer = useRef(null)
  const loader = useRef(null)

  const handleObserver = useCallback(
    (entities) => {
      const target = entities[0]
      if (
        target.isIntersecting &&
        hasMore &&
        !isLoading &&
        !isLoadingMore &&
        onLoadMore
      ) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, isLoadingMore, onLoadMore],
  )

  useEffect(() => {
    // Simple logic: If hasMore is true, set up observer to watch loader element
    if (hasMore && !isLoading && !isLoadingMore) {
      const options = {
        root: null,
        rootMargin: '20px',
        threshold: 1.0,
      }
      observer.current = new IntersectionObserver(handleObserver, options)
      if (loader.current && observer.current) {
        observer.current.observe(loader.current)
      }
    } else {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoading, isLoadingMore, handleObserver])

  const renderTableRows = () =>
    schedulingList.map((booking, index) => {
      const rowKey = booking?._id || booking?.id || `${index}`
      const clientName = booking?.clientName || '-'
      const clientId = booking?.clientId || null
      const eventTitle = booking?.eventTitle || '-'
      const { date: eventDate, time: eventTime } = formatDateTimeWithTimezone(
        booking?.startDateTime,
        timezone,
      )

      return (
        <tr key={rowKey}>
          <td>{index + 1}</td>
          <td className='font-weight-medium'>
            {clientId ? (
              <NavLink
                to={`/coach/clients/${clientId}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-dark-600'>
                {clientName}
              </NavLink>
            ) : (
              clientName
            )}
          </td>
          <td className='text-truncate' style={{ maxWidth: 320 }}>
            {eventTitle}
          </td>
          <td className='text-center'>{eventDate}</td>
          <td className='text-center'>{eventTime}</td>
        </tr>
      )
    })

  return (
    <>
      <section className='scheduling-list-section'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <SchedulingListTitle
            title='Completed Lists'
            showPreview={isCalendarConnected && !!timezone}
            timezone={timezone}
          />
          {clearFilter}
        </div>
        <Table responsive className='custom-table'>
          <thead>
            <tr>
              <th>#</th>
              <th>Client Name</th>
              <th className='text-center'>Event</th>
              <th className='text-center'>Date</th>
              <th className='text-center'>Time</th>
            </tr>
          </thead>
          {isLoading && schedulingList.length === 0 && (
            <tr>
              <td className='text-center border-0 pt-3' colSpan='5'>
                <FeatherLoader />
              </td>
            </tr>
          )}
          {schedulingList.length === 0 && !isLoading && (
            <tr>
              <td className='text-center s6 py-3' colSpan='5'>
                No completed bookings found.
              </td>
            </tr>
          )}
          <tbody>
            {schedulingList?.length > 0 && !isLoading && renderTableRows()}
          </tbody>
          <tfoot>
            <tr ref={loader}>
              <td colSpan='5' className='text-center py-2'>
                {isLoadingMore && <FeatherLoader />}
              </td>
            </tr>
          </tfoot>
        </Table>
      </section>
    </>
  )
}

export default CompletedListView
